import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarDays, ClipboardList, UserCircle, LogOut } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../AuthContext";

const NAV = [
  { to: "/patient", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/patient/visits", label: "My Visits", icon: CalendarDays },
  { to: "/patient/care-plan", label: "Care Plan", icon: ClipboardList },
  { to: "/patient/profile", label: "Profile", icon: UserCircle },
];

export const PatientLayout = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    navigate("/patient/login");
  };

  return (
    <div data-testid="patient-portal" className="min-h-screen bg-brand-paper">
      <header className="sticky top-0 z-40 bg-brand-navy">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <Link to="/patient" className="flex items-center gap-2.5 shrink-0">
            <span className="bg-white rounded-lg p-1"><img src="/assets/bms-logo.png" alt="BMS logo" className="h-8 w-auto" /></span>
            <span className="font-display font-bold text-white text-sm sm:text-base whitespace-nowrap">Patient Portal</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-slate-300 truncate max-w-[180px]">{user?.name}</span>
            <button
              onClick={logout}
              data-testid="patient-logout"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 transition-colors duration-300"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.8} />
              Logout
            </button>
          </div>
        </div>
        <nav className="hidden md:block border-t border-white/10" aria-label="Patient">
          <div className="max-w-5xl mx-auto px-5 flex gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`pt-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 text-[15px] font-semibold border-b-2 transition-colors duration-300 ${
                    isActive ? "border-brand-cyan text-white" : "border-transparent text-slate-300 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-6 md:py-8 pb-28 md:pb-10">
        <Outlet />
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgb(11,27,61,0.08)]" aria-label="Patient mobile">
        <div className="grid grid-cols-4">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`pt-mnav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[11px] font-bold transition-colors duration-300 ${
                  isActive ? "text-brand-teal" : "text-slate-500"
                }`
              }
            >
              <item.icon className="h-5 w-5" strokeWidth={1.8} />
              {item.label === "Care Plan" ? "Care Plan" : item.label === "My Visits" ? "Visits" : item.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};
